'use client';

import * as React from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  BarStack,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

// Shadcn chart primitives (`ChartContainer` provides ChartContext — distinct from `ChartFrame`).
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/hen-ui/shadcn/chart';

import { cn, composeRefs } from '@/hen-ui/utils';

import { ChartLegend } from '../ChartLegend';
import {
  getSafeChartProps,
  useMissingChartConfigWarning,
  buildSafeSeriesKeyMap,
  renderReferenceLines,
  resolveTooltipValueFormat,
  renderChartTick,
  deriveValueAxisDefaults,
  validateTooltipMode,
  renderTooltipFooter,
  usePinnedTooltip,
  useInteractiveLegend,
  X_AXIS_LABEL_MARGIN_PX,
  useXAxisConfig,
  sanitizeTicks,
} from '../chart-utils';
import {
  withSeriesColorScaleDefaults,
  resolveDivergingArmColor,
} from '../chart-palette';
import {
  getDynamicAxisWidth,
  getDynamicXAxisHeight,
  shouldRotateXAxisLabels,
  getXAxisRightMargin,
} from '@/hen-ui/utils/chart';
import type {
  ChartDataPoint,
  ReferenceLineConfig,
  SeriesColorConfig,
  ChartTooltipMode,
  ChartTooltipFooter,
  ColorScale,
  SequentialPalette,
  DivergingPalette,
  XAxisInterval,
} from '../chart-types';
import {
  evaluateColorRules,
  resolveSeriesBaseColor,
  buildTooltipIndicatorColorResolver,
  withSemanticLegendColors,
} from '../chart-color-rules';
import {
  resolveTickFormat,
  resolveTooltipFormat,
  resolveValueFormat,
  type TickFormat,
  type TooltipFormat,
  type ValueFormat,
} from '../number-format';

export interface BarChartProps {
  /** Pre-structured data rows; keys match xAxisKey and the entries in series */
  data: ChartDataPoint[];
  /** Chart config: maps each series key to label + color (shadcn ChartConfig) */
  config: ChartConfig;
  /** Ordered list of series keys to render (one set of bars per key) */
  series: string[];
  /** Key in each data row used for the category axis */
  xAxisKey: string;
  /** Label displayed alongside the X-axis */
  xAxisLabel?: string;
  /** Label displayed alongside the Y-axis */
  yAxisLabel?: string;
  /** Fixed Y-axis range, e.g. [0, 16] for weeks scale */
  yAxisDomain?: [number, number];
  /** Specific tick values on the Y-axis, e.g. [0, 4, 8, 12, 16] */
  yAxisTicks?: number[];
  /**
   * Y-axis tick formatting, as either a formatter function or a
   * {@link NumberFormatSpec} for callers whose props arrive as data.
   */
  yAxisFormat?: ValueFormat;
  /**
   * Formatter for the numeric value rendered in the tooltip. Receives the
   * series name so multi-series charts with heterogeneous units can format
   * per-series. Defaults to `yAxisFormat` if not provided.
   */
  tooltipValueFormat?: (value: number, name: string | number) => string;
  /** Bar grouping mode (default "grouped") */
  mode?: 'grouped' | 'stacked';
  /**
   * Chart orientation: "vertical" = standard vertical bars,
   * "horizontal" = bars extend horizontally (Recharts layout="vertical")
   */
  orientation?: 'vertical' | 'horizontal';
  /**
   * Chart height in px (default 300).
   *
   * - With `fillContainer={false}` (default): fixed at exactly this many pixels.
   * - With `fillContainer={true}`: acts as the **minimum** height. The chart
   *   contributes this value upward to the layout's auto-sizing chain (so the
   *   parent grid row is at least this tall) and stretches beyond it via `h-full`
   *   when a sibling makes the row taller.
   */
  height?: number;
  /**
   * When true, the chart fills its parent container's height. The parent must
   * provide a definite height context (e.g. a flex column where this chart is
   * `flex-1`, or a grid cell). `height` continues to act as the minimum floor.
   */
  fillContainer?: boolean;
  /** Maximum width of a single bar in px (default 40) */
  maxBarSize?: number;
  /**
   * Explicit width of each bar in px. Normally omitted — on the default
   * category X axis Recharts computes bar width from the per-category
   * band and clamps to `maxBarSize`. Set this when `xAxisType="number"`
   * (there's no band to derive a width from, so bars otherwise collapse
   * to ~1px). If omitted while `xAxisType="number"`, the wrapper
   * auto-picks a width based on data length.
   */
  barSize?: number;
  /** Border radius for bar corners in px (default 4) */
  barRadius?: number;
  /** Show legend below chart (default true) */
  showLegend?: boolean;
  /**
   * When `true`, each legend item is an interactive toggle button.
   * Clicking (or pressing Enter / Space) hides / shows that series.
   * Cannot hide the last visible series.
   *
   * Has no effect when `showLegend` is `false` — setting both is a
   * misconfiguration and emits a one-time `console.warn`.
   *
   * **Axis stability:** The value-axis domain remains derived from all
   * declared series (not just visible ones) so the Y-axis does not rescale
   * on toggle. This avoids disorienting jumps and preserves reference-line
   * positions.
   *
   * @default false
   */
  clickableLegend?: boolean;
  /**
   * Sort direction for data rows before rendering.
   * - `'desc'` — highest value first (ranking view)
   * - `'asc'`  — lowest value first
   * - `'none'` — preserve the original array order (default)
   *
   * When `sortBySeries` is provided, rows are ranked by that series key's
   * value. When omitted, rows are ranked by the **sum** of all series values
   * per row so the overall volume drives the order.
   */
  sortBy?: 'asc' | 'desc' | 'none';
  /**
   * Series key to use as the sort criterion when `sortBy` is `'asc'` or
   * `'desc'`. Must be one of the keys present in `series[]`.
   *
   * When omitted (default), the sort falls back to the **sum** of all series
   * values for each row — useful for ranking by total volume.
   *
   * @example sortBySeries="F26" // sort by Fall 2026 values only
   */
  sortBySeries?: string;
  /**
   * Optional reference/target lines drawn on top of the chart
   * (e.g. "target acceptance rate: 75%"). The helper maps `axis` based
   * on orientation so `axis: 'value'` always hits the measured axis.
   */
  referenceLines?: ReferenceLineConfig[];
  /**
   * Override the default character-count limit (30) used when truncating
   * x-axis labels. Values below 4 are ignored (falls back to the library
   * default). Useful for charts with long category names (e.g. county names).
   */
  xAxisLabelMaxLength?: number;
  /**
   * Per-series color configuration for conditional coloring. Maps series
   * key to its `SeriesColorConfig` (semantic color and/or color rules).
   * When provided, bars are rendered with per-point `<Cell>` fills.
   */
  seriesColorConfigs?: Record<string, SeriesColorConfig>;
  /**
   * Color-scale mode for the per-series default color (default
   * `'categorical'`). `'sequential'` spreads a light→dark gradient across the
   * visible series; `'diverging'` colors each bar by its value's sign. Semantic
   * rules (`seriesColorConfigs`) and explicit `config[key].color` still win.
   * Omitting this renders byte-identically to categorical.
   */
  colorScale?: ColorScale;
  /** Sequential palette family when `colorScale='sequential'` (default `'blues'`). */
  sequentialPalette?: SequentialPalette;
  /** Diverging palette family when `colorScale='diverging'` (default `'blueOrange'`). */
  divergingPalette?: DivergingPalette;
  /**
   * Preferred number of ticks on the X-axis. Recharts uses this as a hint
   * and may adjust to produce "nice" tick values. Only applies to numeric
   * X-axes; ignored for categorical axes (one tick per category).
   *
   * **To use this prop, set `xAxisType="number"`.** Otherwise the axis is
   * categorical and Recharts silently ignores `tickCount` — reach for
   * `xAxisInterval` to thin labels on category axes instead. Setting
   * `xAxisTickCount` on a category axis emits a dev-mode warn telling
   * the operator the prop was ignored (so it doesn't look like the prop
   * is broken).
   *
   * Values outside [2, 50] are clamped with a console warning.
   * When both `xAxisTickCount` and `xAxisInterval` are set on a numeric
   * axis, tickCount wins (interval is dropped — Recharts ignores
   * `interval` on numeric axes anyway). On category axes both props are
   * passed through but Recharts uses only `interval`.
   */
  xAxisTickCount?: number;
  /**
   * Controls which X-axis tick labels are shown. Only applies to
   * categorical X-axes; ignored for numeric axes (use `xAxisTickCount`
   * there). Setting `xAxisInterval` on a numeric axis emits a dev-mode
   * warn telling the operator the prop was ignored.
   *
   * On category axes, when both `xAxisTickCount` and `xAxisInterval` are
   * set, `xAxisInterval` wins (the tickCount value is ignored by Recharts).
   *
   * @see XAxisInterval for the full union (number | 'preserveStart' | …).
   */
  xAxisInterval?: XAxisInterval;
  /**
   * Formatting for X-axis tick labels (not the axis title), as either a
   * formatter function or a {@link NumberFormatSpec}.
   *
   * @see XAxisTickFormatter for the function signature and usage examples.
   */
  xAxisTickFormat?: TickFormat;
  /**
   * When true, shows the X-axis line. When false, hides the axis line
   * while keeping tick marks and labels.
   *
   * @default false (matches existing behavior)
   */
  xAxisLineVisible?: boolean;
  /**
   * Recharts X-axis `type`. Defaults to `'category'`, which is what almost
   * all BarChart usage wants (string labels per bar). Switch to `'number'`
   * when your `xAxisKey` points to numeric values that should be plotted
   * on a continuous scale — this is the only mode in which
   * `xAxisTickCount` has a visible effect.
   *
   * **Numeric-X note:** Recharts has no per-category band on a numeric
   * axis, so bar width can't be auto-derived. When you don't pass an
   * explicit `barSize`, the wrapper picks a reasonable default based on
   * data length so bars don't collapse to ~1px.
   *
   * @default 'category'
   */
  xAxisType?: 'category' | 'number';
  /**
   * Controls when the data-point tooltip is shown.
   * - `'hover'`    — standard hover tooltip (default).
   * - `'pinned'`   — tooltip appears on click and stays until the user
   *                  clicks elsewhere on the chart or presses Escape.
   * - `'disabled'` — tooltip is suppressed entirely.
   */
  tooltipMode?: ChartTooltipMode;
  /**
   * Row-aware value formatter for the tooltip, as either a function
   * receiving `(value, seriesKey, row)` or a {@link ChartTooltipDeltaSpec}.
   * Takes precedence over `tooltipValueFormat` when both are provided. Only
   * replaces the value cell — the indicator dot and series label remain.
   */
  tooltipFormatter?: TooltipFormat;
  /**
   * Methodology copy rendered as a footer note inside the tooltip bubble.
   * - Plain `string`    — rendered verbatim.
   * - `{ md: string }` — parsed for `**bold**`, `*italic*`, `[text](url)`.
   */
  tooltipContent?: ChartTooltipFooter;
}

const EMPTY_FALLBACK_TEXT = 'No data available';

// EDUN-11255 fallback — used only when `deriveValueAxisDefaults` returns
// `undefined` (empty data, single-zero data). Bars communicate magnitude
// from a baseline, so the value axis must include zero; Recharts' own
// default of `['auto', 'auto']` (= `[dataMin, dataMax]`) gives the
// smallest-value bar a length of 0 and makes it invisible. The deriver
// already anchors at zero in the common path; this constant guards the
// cases where the deriver bows out.
const VALUE_AXIS_DEFAULT_DOMAIN: [number, 'auto'] = [0, 'auto'];

/**
 * BarChart — hen-ui composite wrapping Recharts via the atomic Chart
 * primitive (shadcn-style). Supports grouped, stacked, vertical, and
 * horizontal modes. Stateless, prop-driven, theme-aware via ChartConfig
 * + scoped `--color-<key>` CSS variables.
 *
 * In stacked mode, bars are wrapped in a Recharts `<BarStack>` so the
 * `radius` applies to the outer shell of the stack only — middle segments
 * render with square corners, as intended.
 */
export const BarChart = React.forwardRef<HTMLDivElement, BarChartProps>(
  (
    {
      data: rawData,
      config,
      series,
      xAxisKey,
      xAxisLabel,
      yAxisLabel,
      yAxisDomain: rawDomain,
      yAxisTicks: rawTicks,
      yAxisFormat: rawYAxisFormat,
      tooltipValueFormat,
      mode = 'grouped',
      orientation = 'vertical',
      height = 300,
      fillContainer = false,
      maxBarSize = 40,
      barSize: rawBarSize,
      barRadius = 4,
      showLegend = true,
      clickableLegend = false,
      sortBy = 'none',
      sortBySeries,
      referenceLines,
      xAxisLabelMaxLength,
      seriesColorConfigs,
      colorScale = 'categorical',
      sequentialPalette,
      divergingPalette,
      xAxisTickCount: rawXAxisTickCount,
      xAxisInterval: rawXAxisInterval,
      xAxisTickFormat: rawXAxisTickFormat,
      xAxisLineVisible = false,
      xAxisType = 'category',
      tooltipMode,
      tooltipFormatter: rawTooltipFormatter,
      tooltipContent,
    },
    ref,
  ) => {
    const yAxisFormat = React.useMemo(
      () => resolveValueFormat(rawYAxisFormat),
      [rawYAxisFormat],
    );
    const xAxisTickFormat = React.useMemo(
      () => resolveTickFormat(rawXAxisTickFormat),
      [rawXAxisTickFormat],
    );
    const tooltipFormatter = React.useMemo(
      () => resolveTooltipFormat(rawTooltipFormatter, yAxisFormat),
      [rawTooltipFormatter, yAxisFormat],
    );

    const {
      data,
      yAxisDomain,
      yAxisTicks: rawYAxisTicks,
    } = getSafeChartProps({
      data: rawData,
      yAxisDomain: rawDomain,
      yAxisTicks: rawTicks,
    });

    // EDUN-12034 — drop NaN / non-numbers (with per-entry warn), dedupe, and
    // sort the consumer-supplied tick list. `[]` and all-invalid inputs
    // collapse to `undefined` so the downstream auto-deriver takes over,
    // keeping behavior symmetric with LineChart's yAxisTicks contract.
    const yAxisTicks = React.useMemo(
      () => sanitizeTicks(rawYAxisTicks, 'BarChart'),
      [rawYAxisTicks],
    );

    const sortedData = React.useMemo(() => {
      if (sortBy === 'none') return data;
      return [...data].sort((a, b) => {
        const valA = sortBySeries
          ? typeof a[sortBySeries] === 'number'
            ? (a[sortBySeries] as number)
            : 0
          : series.reduce(
              (sum, key) =>
                sum + (typeof a[key] === 'number' ? (a[key] as number) : 0),
              0,
            );
        const valB = sortBySeries
          ? typeof b[sortBySeries] === 'number'
            ? (b[sortBySeries] as number)
            : 0
          : series.reduce(
              (sum, key) =>
                sum + (typeof b[key] === 'number' ? (b[key] as number) : 0),
              0,
            );
        return sortBy === 'desc' ? valB - valA : valA - valB;
      });
    }, [data, series, sortBy, sortBySeries]);

    // Sanitize series keys into CSS-custom-property-safe tokens. Human-readable
    // column names like "Fall 2026" produce `--color-Fall 2026` — an invalid CSS
    // declaration that the browser silently drops, leaving the series black with
    // an empty legend swatch. `dataKey` on each <Bar> keeps the raw key so
    // Recharts reads the correct data column; `name`, config keys, and
    // `var(--color-*)` references all use the safe token throughout.
    const instanceId = React.useId();
    const safeKeyMap = React.useMemo(
      () => buildSafeSeriesKeyMap(series),
      [series],
    );
    const safeSeries = React.useMemo(
      () => series.map((k) => safeKeyMap.get(k) ?? k),
      [series, safeKeyMap],
    );
    const safeConfig = React.useMemo<ChartConfig>(() => {
      const next: ChartConfig = {};
      for (const rawKey of series) {
        const safeKey = safeKeyMap.get(rawKey) ?? rawKey;
        const entry = config[rawKey];
        if (entry) {
          next[safeKey] =
            safeKey !== rawKey
              ? { ...entry, label: entry.label ?? rawKey }
              : entry;
        } else if (safeKey !== rawKey) {
          next[safeKey] = { label: rawKey };
        }
      }
      return next;
    }, [series, config, safeKeyMap]);
    const safeSeriesColorConfigs = React.useMemo<
      Record<string, SeriesColorConfig> | undefined
    >(() => {
      if (!seriesColorConfigs) return undefined;
      const next: Record<string, SeriesColorConfig> = {};
      for (const [rawKey, val] of Object.entries(seriesColorConfigs)) {
        next[safeKeyMap.get(rawKey) ?? rawKey] = val;
      }
      return next;
    }, [seriesColorConfigs, safeKeyMap]);

    useMissingChartConfigWarning('BarChart', config, series);

    // EDUN-12033 — validate + resolve the four interlocking X-axis props
    // (`type`, `domain`, `tickCount`, `interval`) in one hook. Emits warnings
    // for clamped / invalid / conflicting input and encapsulates the
    // back-compat rule (`interval={0}` default when caller did not supply
    // one). Returned values are spread directly onto `<XAxis>` below.
    const xAxisConfig = useXAxisConfig({
      rawTickCount: rawXAxisTickCount,
      rawInterval: rawXAxisInterval,
      xAxisType,
      chartName: 'BarChart',
    });

    const { hiddenSeries, handleSeriesToggle } = useInteractiveLegend({
      series: safeSeries,
      showLegend,
      clickableLegend,
      chartName: 'BarChart',
    });

    // Auto-fill missing per-series colors based on `colorScale`. Categorical
    // (default) delegates to the categorical palette indexed by position in
    // `series[]` — byte-identical to historical behavior. Sequential spreads a
    // gradient across the visible series; diverging seeds the high-arm base
    // color (per-cell sign mapping is applied in `renderBars`). Explicit
    // `config[key].color` still wins.
    const resolvedConfig = React.useMemo(
      () =>
        withSeriesColorScaleDefaults(safeConfig, safeSeries, {
          colorScale,
          sequentialPalette,
          divergingPalette,
          hiddenSeries,
        }),
      [
        safeConfig,
        safeSeries,
        colorScale,
        sequentialPalette,
        divergingPalette,
        hiddenSeries,
      ],
    );

    const isDiverging = colorScale === 'diverging';

    // Mirror per-series `semanticColor` overrides into the config the
    // `<ChartLegend>` reads from, so the legend swatch matches the rendered
    // bar color. Series with only `colorRules` (no semanticColor)
    // intentionally keep the base palette color in the legend.
    const legendConfig = React.useMemo(
      () =>
        withSemanticLegendColors(
          resolvedConfig,
          safeSeriesColorConfigs,
          safeSeries,
        ),
      [resolvedConfig, safeSeriesColorConfigs, safeSeries],
    );

    // Dev-only guard: warn if sortBySeries is set to a key not in series[].
    const warnedSortBySeries = React.useRef<Set<string>>(new Set());
    if (
      process.env.NODE_ENV !== 'production' &&
      sortBySeries !== undefined &&
      !series.includes(sortBySeries) &&
      !warnedSortBySeries.current.has(sortBySeries)
    ) {
      warnedSortBySeries.current.add(sortBySeries);
      console.warn(
        `[hen-ui/BarChart] sortBySeries="${sortBySeries}" is not in the series array [${series.map((k) => `"${k}"`).join(', ')}]. All rows will sort as 0 — check the key name.`,
      );
    }

    const isHorizontal = orientation === 'horizontal';

    // Determine if vertical chart labels need rotation to prevent overlap
    const needsRotation =
      !isHorizontal && shouldRotateXAxisLabels(sortedData, xAxisKey);

    // Resolve barSize. On a numeric X axis Recharts has no per-category band
    // to size bars to, so they collapse to ~1px unless an explicit width is
    // provided. Auto-pick a reasonable width based on data length when the
    // caller hasn't specified one; clamp to `maxBarSize` so very small data
    // sets don't produce overly chunky bars.
    //
    // **Caveat:** the heuristic assumes a ~600px plot area (the dashboard's
    // typical chart-cell width). For unusually narrow (<300px sidebar widgets)
    // or wide (>900px hero charts) containers, the auto-pick will be too thick
    // or too thin respectively — set an explicit `barSize` prop in those
    // layouts. We can't read the ResponsiveContainer's actual width from this
    // synchronous useMemo, so the heuristic is intentionally conservative
    // (clamped at both ends) rather than precise.
    //
    // @todo (EDUN-12033 follow-up) — measure the rendered plot width via
    // `<ResponsiveContainer>`'s `onResize` callback (or a `ResizeObserver`
    // on the wrapper ref) and feed it back into the picker so the heuristic
    // self-corrects on mount + window resize. Until then, the constant
    // below is the dashboard-cell median, exposed as a named identifier so
    // it can be tuned (or replaced with a measurement) in one place.
    const DEFAULT_DASHBOARD_PLOT_WIDTH_PX = 600;
    const resolvedBarSize = React.useMemo<number | undefined>(() => {
      if (rawBarSize !== undefined) return rawBarSize;
      if (xAxisType !== 'number' || isHorizontal) return undefined;
      const n = Math.max(1, sortedData.length);
      // 70% of the per-point slot for the bar, 30% for inter-bar gap.
      // Clamped between 4px (legibility floor) and `maxBarSize` (so few-bar
      // charts don't render absurdly chunky bars).
      const slot = DEFAULT_DASHBOARD_PLOT_WIDTH_PX / n;
      return Math.max(4, Math.min(maxBarSize, Math.round(slot * 0.7)));
    }, [rawBarSize, xAxisType, isHorizontal, sortedData.length, maxBarSize]);

    // For horizontal orientation, calculate dynamic axis width based on longest label.
    // This prevents truncation/wrapping by adapting to the actual data. Also truncate
    // extremely long labels as a fallback to keep the chart focused on data.
    const { axisWidth } = isHorizontal
      ? getDynamicAxisWidth(sortedData, xAxisKey)
      : { axisWidth: 0 }; // Not used for vertical

    // For vertical orientation with rotated labels, calculate X-axis height dynamically
    // with a reasonable cap to prevent infinite stretching. Also truncate extremely long
    // labels as a fallback to keep the chart focused on data.
    const getXAxisHeight = (): number => {
      if (needsRotation) {
        return getDynamicXAxisHeight(sortedData, xAxisKey, !!xAxisLabel);
      }
      return xAxisLabel ? 80 : 60;
    };

    const xAxisHeight = getXAxisHeight();

    // In fillContainer mode the chart contributes `height` upward as min-height
    // (so the row is sized from content, same as fixed-pixel mode) and uses
    // `h-full` to stretch when a sibling makes the row taller. In fixed mode it
    // pins to exactly `height`.
    const sizingStyle: React.CSSProperties = fillContainer
      ? { minHeight: height }
      : { height };
    const sizingClass = fillContainer ? 'h-full' : undefined;

    const isStacked = mode === 'stacked';

    // EDUN-11255 — auto-derive value-axis domain + ticks from observed
    // values when the consumer has not pinned both. In stacked mode the
    // visual maximum is the per-row sum (each row stacks); in grouped
    // mode the maximum is per-cell. Reference lines on the value axis
    // also extend the candidate set so a target line never lands off the
    // visible domain.
    //
    // Kept above the `!data.length` early return so the hook fires on
    // every render regardless of branch — required by the rules of hooks.
    const valueAxisDefaults = React.useMemo(() => {
      if (yAxisDomain && yAxisTicks) return undefined;

      const cellValues = sortedData.flatMap((row) =>
        series.map((key) =>
          typeof row[key] === 'number' ? (row[key] as number) : 0,
        ),
      );
      const stackTotals = isStacked
        ? sortedData.map((row) =>
            series.reduce(
              (sum, key) =>
                sum + (typeof row[key] === 'number' ? (row[key] as number) : 0),
              0,
            ),
          )
        : [];
      const referenceHits = (referenceLines ?? [])
        .filter((rl) => (rl.axis ?? 'value') === 'value')
        .map((rl) => Number(rl.at))
        .filter(Number.isFinite);

      return deriveValueAxisDefaults([
        ...(isStacked ? stackTotals : cellValues),
        ...referenceHits,
      ]);
    }, [
      sortedData,
      series,
      isStacked,
      referenceLines,
      yAxisDomain,
      yAxisTicks,
    ]);

    // Consumer overrides win in this order: explicit prop > derived > fallback.
    const valueAxisDomain =
      yAxisDomain ?? valueAxisDefaults?.domain ?? VALUE_AXIS_DEFAULT_DOMAIN;
    const valueAxisTicks = yAxisTicks ?? valueAxisDefaults?.ticks;

    // Store validated tooltip mode once per render to avoid duplicate validation and warnings.
    const resolvedTooltipMode = validateTooltipMode(tooltipMode);

    // Must be called before the early return (rules of hooks).
    const {
      tooltipActive,
      onChartClick,
      containerRef: pinnedRef,
    } = usePinnedTooltip(resolvedTooltipMode);
    const rootRef = React.useMemo(
      () => composeRefs<HTMLDivElement>(pinnedRef, ref),
      [pinnedRef, ref],
    );

    if (!data.length) {
      // See LineChart for rationale: intentionally no `role`, since this is
      // static text rather than an image or live region.
      return (
        <div
          ref={ref}
          aria-label={EMPTY_FALLBACK_TEXT}
          className={cn(
            'flex items-center justify-center rounded bg-neutral-50 text-neutral-500 text-b4',
            sizingClass,
          )}
          style={sizingStyle}
        >
          {EMPTY_FALLBACK_TEXT}
        </div>
      );
    }

    // Radius is applied by BarStack when stacked; otherwise per individual Bar.
    const radius: [number, number, number, number] = isHorizontal
      ? [0, barRadius, barRadius, 0]
      : [barRadius, barRadius, 0, 0];

    const renderBars = () => {
      const bars = series
        .map((rawKey, i) => ({ rawKey, safeKey: safeSeries[i] }))
        .filter(({ safeKey }) => !hiddenSeries.has(safeKey))
        .map(({ rawKey, safeKey }) => {
          // Gate per-series, not globally: a series with only a STATIC
          // `semanticColor` (positive, negative, info, …) gets its color
          // from the `<Bar fill>` prop directly via
          // `resolveSeriesBaseColor`. Emitting `<Cell>` children for it
          // would allocate `data.length` cells per render that all
          // resolve to the same semantic color the `<Bar>` already uses —
          // wasted reconciliation work that erodes the perf-test budget.
          //
          // `'auto-by-value'` is the exception: it has no single
          // representative color (positive ≥0 / negative <0 must be
          // resolved per data point), so it MUST go through the per-cell
          // path even without `colorRules`.
          const cfg = safeSeriesColorConfigs?.[safeKey];
          // Single narrowed local: non-null exactly when we need per-cell
          // fills. Carrying `cfg` through the gate (rather than a boolean
          // flag) lets TypeScript narrow `perCellCfg` inside the map
          // below so we can read `colorRules` / `semanticColor` without
          // non-null assertions.
          const perCellCfg =
            cfg &&
            (!!cfg.colorRules?.length || cfg.semanticColor === 'auto-by-value')
              ? cfg
              : null;
          // `diverging` colors each bar by its value's sign, so it also needs
          // per-`<Cell>` fills even when no `seriesColorConfigs` are set. The
          // diverging arm color becomes the per-cell *default* fed to
          // `evaluateColorRules`, so semantic rules still win on top of it.
          const needsPerCell = perCellCfg !== null || isDiverging;
          return (
            <Bar
              key={safeKey}
              dataKey={rawKey}
              name={safeKey}
              fill={resolveSeriesBaseColor(safeKey, safeSeriesColorConfigs)}
              radius={isStacked ? 0 : radius}
            >
              {needsPerCell &&
                sortedData.map((entry, index) => {
                  const value =
                    typeof entry[rawKey] === 'number'
                      ? (entry[rawKey] as number)
                      : null;
                  const defaultColor = isDiverging
                    ? (resolveDivergingArmColor(value, divergingPalette) ??
                      `var(--color-${safeKey})`)
                    : `var(--color-${safeKey})`;
                  return (
                    <Cell
                      key={index}
                      fill={evaluateColorRules(
                        cfg?.colorRules,
                        value,
                        cfg?.semanticColor,
                        defaultColor,
                      )}
                    />
                  );
                })}
            </Bar>
          );
        });

      if (!isStacked) return bars;

      return (
        <BarStack stackId={`stack-${instanceId}`} radius={radius}>
          {bars}
        </BarStack>
      );
    };

    // `role='figure'` (not `role='img'`): see LineChart for full rationale.
    // Summary: `img` prunes the subtree; Recharts' accessibilityLayer lives
    // on an inner `role='application'` SVG that needs to remain exposed.
    return (
      <div
        ref={rootRef}
        role='figure'
        aria-label='Bar chart'
        className={sizingClass}
        style={sizingStyle}
      >
        <ChartContainer
          config={resolvedConfig}
          className='aspect-auto h-full w-full'
        >
          <RechartsBarChart
            data={sortedData}
            layout={isHorizontal ? 'vertical' : 'horizontal'}
            maxBarSize={maxBarSize}
            barSize={resolvedBarSize}
            accessibilityLayer
            onClick={onChartClick}
            margin={
              isHorizontal
                ? undefined
                : {
                    right: getXAxisRightMargin(
                      sortedData,
                      xAxisKey,
                      needsRotation,
                    ),
                    bottom:
                      !showLegend && xAxisLabel ? X_AXIS_LABEL_MARGIN_PX : 0,
                  }
            }
          >
            <CartesianGrid strokeDasharray='3 3' />

            {isHorizontal ? (
              <>
                <XAxis
                  type='number'
                  height={yAxisLabel ? 50 : 30}
                  domain={valueAxisDomain}
                  ticks={valueAxisTicks}
                  tickFormatter={yAxisFormat}
                  label={
                    yAxisLabel
                      ? {
                          value: yAxisLabel,
                          position: 'insideBottom',
                          offset: -5,
                        }
                      : undefined
                  }
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  type='category'
                  dataKey={xAxisKey}
                  interval={0}
                  tick={(p) =>
                    renderChartTick({
                      ...p,
                      dy: '0.355em',
                      textAnchor: 'end',
                      maxLabelLength: xAxisLabelMaxLength,
                      tickFormatter: xAxisTickFormat,
                    })
                  }
                  label={
                    xAxisLabel
                      ? {
                          value: xAxisLabel,
                          angle: -90,
                          position: 'insideLeft',
                          style: { textAnchor: 'middle' },
                        }
                      : undefined
                  }
                  tickLine={false}
                  axisLine={false}
                  width={axisWidth}
                />
              </>
            ) : (
              <>
                <XAxis
                  dataKey={xAxisKey}
                  // `type`, `domain`, `tickCount`, and `interval` come from
                  // `useXAxisConfig` so the precedence rules (tickCount wins;
                  // `interval={0}` default for back-compat; numeric-axis
                  // domain → `['dataMin','dataMax']`) live in one place
                  // across charts.
                  type={xAxisConfig.type}
                  domain={xAxisConfig.domain}
                  height={xAxisHeight}
                  tickCount={xAxisConfig.tickCount}
                  interval={xAxisConfig.interval}
                  tick={(p) =>
                    renderChartTick({
                      ...p,
                      rotate: needsRotation,
                      textAnchor: needsRotation ? 'end' : 'middle',
                      maxLabelLength: xAxisLabelMaxLength,
                      tickFormatter: xAxisTickFormat,
                    })
                  }
                  label={
                    xAxisLabel
                      ? {
                          value: xAxisLabel,
                          position: 'insideBottom',
                          offset: -5,
                        }
                      : undefined
                  }
                  tickLine={false}
                  axisLine={xAxisLineVisible}
                />
                <YAxis
                  domain={valueAxisDomain}
                  ticks={valueAxisTicks}
                  tickFormatter={yAxisFormat}
                  label={
                    yAxisLabel
                      ? {
                          value: yAxisLabel,
                          angle: -90,
                          position: 'insideLeft',
                          style: { textAnchor: 'middle' },
                        }
                      : undefined
                  }
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
              </>
            )}

            {resolvedTooltipMode !== 'disabled' && (
              <ChartTooltip
                cursor={false}
                active={tooltipActive}
                trigger={resolvedTooltipMode === 'pinned' ? 'click' : undefined}
                content={
                  <ChartTooltipContent
                    indicator='dot'
                    valueFormatter={
                      tooltipFormatter
                        ? undefined
                        : resolveTooltipValueFormat(
                            tooltipValueFormat,
                            yAxisFormat,
                          )
                    }
                    rowValueFormatter={tooltipFormatter}
                    footer={renderTooltipFooter(tooltipContent)}
                    getItemIndicatorColor={buildTooltipIndicatorColorResolver(
                      safeSeriesColorConfigs,
                    )}
                  />
                }
              />
            )}
            {showLegend && (
              <ChartLegend
                series={safeSeries}
                config={legendConfig}
                seriesColorConfigs={safeSeriesColorConfigs}
                colorScale={colorScale}
                divergingPalette={divergingPalette}
                clickableLegend={clickableLegend}
                hiddenSeries={hiddenSeries}
                onSeriesToggle={handleSeriesToggle}
                verticalAlign='bottom'
                align='center'
                wrapperStyle={
                  (isHorizontal ? yAxisLabel : xAxisLabel)
                    ? { paddingTop: 12 }
                    : undefined
                }
              />
            )}

            {renderBars()}
            {renderReferenceLines(referenceLines, orientation)}
          </RechartsBarChart>
        </ChartContainer>
      </div>
    );
  },
);
BarChart.displayName = 'BarChart';
